import React, { useCallback, useContext, useMemo, useState } from "react";

import type { IssueRow } from "#app/lib/data";
import { useIssueCardActions } from "#app/hooks/kanban/use-issue-card-actions";
import type { Omittable } from "@salestrak/ui";

/**
 * Inline rename — the row/card menu's Rename puts that issue's own name text
 * into edit mode in place. The state travels by CONTEXT, never through
 * column defs or card props: a def rebuilt with the renaming id is a new
 * cell element TYPE, so every cell remounts on rename start — which unmounts
 * the editor the click just opened (the flexRender remount trap). Hosts
 * without an inline affordance mount no provider; consumers read it through
 * `useIssueRenameOptional()` and fall back (the menu opens the project and
 * focuses its title input).
 */
export type IssueRenameControls = Omittable<{
  renamingIssueId: string | null;
  onRenameStart: (issueId: string) => void;
  onRenameCommit: (issue: IssueRow, name: string) => void;
  onRenamingEnd: () => void;
}>;

const IssueRenameContext = React.createContext<IssueRenameControls | null>(
  null,
);

export function IssueRenameProvider({
  value,
  children,
}: {
  value: IssueRenameControls;
  children: React.ReactNode;
}) {
  return (
    <IssueRenameContext.Provider value={value}>
      {children}
    </IssueRenameContext.Provider>
  );
}

/** Null outside a provider — the host has no inline rename surface. */
export function useIssueRenameOptional(): IssueRenameControls | null {
  return useContext(IssueRenameContext);
}

/**
 * The one rename state machine a hosting view mounts: the renaming id, the
 * trimmed/changed-only commit through the optimistic rename mutation, and
 * the end callback. Both issue views (list and board) provide the same
 * controls, so the menu's Rename behaves identically over either.
 */
export function useIssueRenameControls(): IssueRenameControls {
  const [renamingIssueId, setRenamingIssueId] = useState<string | null>(null);
  const { renameIssue } = useIssueCardActions();
  const onRenameStart = useCallback(
    (issueId: string) => setRenamingIssueId(issueId),
    [],
  );
  const onRenameCommit = useCallback(
    (issue: IssueRow, next: string) => {
      const trimmed = next.trim();
      if (trimmed && trimmed !== issue.name) {
        void renameIssue(issue.issueId, trimmed);
      }
    },
    [renameIssue],
  );
  const onRenamingEnd = useCallback(() => setRenamingIssueId(null), []);
  return useMemo(
    () => ({ renamingIssueId, onRenameStart, onRenameCommit, onRenamingEnd }),
    [renamingIssueId, onRenameStart, onRenameCommit, onRenamingEnd],
  );
}
